/**
 * A unit's stats: damage, hp, range, speed, sight and upkeep (rank bonus in
 * green), what it is strong and weak against, and its rank and xp. Without a
 * `unit` it is the type's base stats (a Train card hover); with one, the live
 * values. The numbers come from `view/unitStats.ts`. `compact` drops the
 * xp explanation and the weak-vs line (the lone-unit dock).
 */
import type { RuleSet } from '@/game/rules';
import type { Unit } from '@/game/sim';
import { chevrons } from '@/game/view/activity';
import {
  type BoostedStat,
  type Counter,
  type Matchup,
  fmt as num,
  targetsText,
  type UnitStats,
  unitStats,
  upkeepText,
  xpToNextText,
} from '@/game/view/unitStats';
import { VERBS } from '@/game/view/verbs';
import { VerbLabel } from './VerbLabel';

export function Stat({
  label,
  children,
  bonus,
}: {
  label: string;
  children: React.ReactNode;
  bonus?: number;
}) {
  return (
    <div className="min-w-0 rounded-lg bg-fill px-2 py-0.5 leading-tight">
      <small className="block text-[10px] text-fg-subtle">{label}</small>
      <b className="text-xs font-medium tabular-nums">
        {children}
        {bonus !== undefined && bonus > 0.005 && (
          <i className="ml-1 text-[10px] font-normal not-italic text-ok">+{num(bonus)}</i>
        )}
      </b>
    </div>
  );
}

function boosted(stat: BoostedStat, unit = '') {
  return `${num(stat.value)}${unit}`;
}

function MatchupLine({
  label,
  items,
  detail,
}: {
  label: string;
  items: (Matchup & { kind?: Counter['kind']; dps?: number })[];
  detail: (m: Matchup & { dps?: number }) => string;
}) {
  if (items.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-1 text-[11px] text-fg-muted">
      <span className="text-fg-subtle">{label}</span>
      {items.map((m) => (
        <span key={m.type} className="chip px-1.5 py-0 text-xs tabular-nums">
          {m.kind === 'buildings' ? (
            <span className="font-medium">{m.name}</span>
          ) : (
            <span className="font-medium">{m.glyph}</span>
          )}{' '}
          {detail(m)}
        </span>
      ))}
    </p>
  );
}

function Xp({ stats, compact }: { stats: UnitStats; compact: boolean }) {
  const xp = stats.xp;
  if (!xp) return null;
  const top = xp.thresholds[xp.thresholds.length - 1] ?? 1;
  const live = stats.hp !== null;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wider text-fg-subtle">
        <span>
          <VerbLabel verb="rankUp">{VERBS.rankUp.label} by xp</VerbLabel>
        </span>
        {live && (
          <span className="text-xs normal-case tracking-normal tabular-nums">
            <b className="font-medium text-fg">{Math.floor(xp.xp)}</b>
            {xp.next !== null && <span> / {xp.next} xp</span>}
          </span>
        )}
      </div>
      <div
        role="progressbar"
        aria-label={`${stats.name} experience`}
        aria-valuemin={0}
        aria-valuemax={top}
        aria-valuenow={Math.min(Math.floor(xp.xp), top)}
        className="relative h-1 rounded-full bg-fill"
      >
        <div
          className="h-full rounded-full bg-warn"
          style={{ width: `${Math.round(xp.fraction * 100)}%` }}
        />
        {xp.thresholds.map((t) => (
          <span
            key={t}
            aria-hidden
            className="absolute top-[-1px] h-1.5 w-px bg-fg-subtle"
            style={{ left: `${(t / top) * 100}%` }}
          />
        ))}
      </div>
      <div className="flex justify-between text-xs tabular-nums text-fg-subtle">
        {xp.thresholds.map((t, i) => (
          <span key={t} className={xp.rank > i ? 'text-warn' : undefined}>
            {chevrons(i + 1)} {t}
            {xp.rank > i && ' ✓'}
          </span>
        ))}
      </div>
      {!compact && (
        <p className="text-[11px] text-fg-muted">
          {live && `${xpToNextText(xp)}. `}
          Earned by{' '}
          {xp.earns.map((e) => `${e.label} (${num(e.per)} xp each)`).join(', ') || 'training'}.
        </p>
      )}
    </div>
  );
}

export function UnitCard({
  id,
  rules,
  type,
  unit,
  compact = false,
  hideHeader = false,
  className = '',
}: {
  /** DOM id, for aria-describedby. */
  id?: string;
  rules: RuleSet;
  type: string;
  unit?: Unit;
  compact?: boolean;
  /** Leave out the name / rank / hp row (the dock's unit chip already says it). */
  hideHeader?: boolean;
  className?: string;
}) {
  const stats = unitStats(rules, type, unit);
  if (!stats) return null;
  const upkeep = upkeepText(stats.upkeep);
  const targets = targetsText(stats);
  return (
    <div id={id} className={`flex flex-col gap-1.5 text-xs ${className}`}>
      {!hideHeader && (
        <div className="flex items-center gap-2">
          <span className="panel-title">{stats.name}</span>
          {stats.rank > 0 && (
            <span className="chip text-warn">
              Rank {stats.rank} · {chevrons(stats.rank)}
            </span>
          )}
          {stats.hp !== null && (
            <span className="tabular-nums">
              {Math.ceil(stats.hp)}
              <span className="text-fg-subtle">/{Math.ceil(stats.maxHp.value)} hp</span>
            </span>
          )}
          {stats.mechanical && <span className="chip">machine</span>}
        </div>
      )}
      <div className="grid grid-cols-3 gap-1">
        {stats.damage && (
          <Stat label="Damage" bonus={stats.damage.bonus}>
            {boosted(stats.damage, '/s')}
          </Stat>
        )}
        <Stat label="Max hp" bonus={stats.maxHp.bonus}>
          {boosted(stats.maxHp)}
        </Stat>
        {stats.heal && (
          <Stat label="Heals" bonus={stats.heal.bonus}>
            {boosted(stats.heal, ' hp/s')}
          </Stat>
        )}
        {(stats.damage || stats.heal) && (
          <Stat label="Range">
            {stats.range}
            {stats.range === 1 ? ' melee' : stats.shot ? ` ${stats.shot}` : ''}
          </Stat>
        )}
        <Stat label="Speed">{num(stats.speed)}×</Stat>
        <Stat label="Sight">{stats.sight} tiles</Stat>
        <Stat label="Upkeep (base)">{upkeep || 'none'}</Stat>
      </div>
      {(targets || stats.heal || stats.mechanical) && (
        <p className="text-[11px] text-fg-muted">
          {[
            targets && `Targets: ${targets.toLowerCase()}`,
            stats.heal && `Mends ${stats.heal.whom}`,
            stats.mechanical && 'Mended by engineers, not healers',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      <MatchupLine
        label="Strong vs"
        items={[...stats.strongVs, ...(stats.vsBuildings ? [stats.vsBuildings] : [])]}
        detail={(m) =>
          `${m.name === 'Buildings' ? 'Bldg' : ''} ×${num(m.mult)} · ${num(m.dps ?? 0)}/s`
        }
      />
      {!compact && (
        <MatchupLine label="Weak vs" items={stats.weakVs} detail={(m) => `×${num(m.mult)}`} />
      )}
      {stats.weakVsBuildings !== null && (
        <p className="text-[11px] text-fg-muted">
          Weak vs buildings: ×{num(stats.weakVsBuildings)} damage
        </p>
      )}
      <Xp stats={stats} compact={compact} />
    </div>
  );
}
